import { Account, Allocation, GUID, ManagementType } from '@ndvr/portfolio-api';

import { CopiableGuid } from '@/components/CopiableGuid';
import { StatusBadge } from '@/components/StatusBadge';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { titleCase } from 'utils';
import { StrategyAllocations } from 'views/portfolio-engine/allocation-builder/allocations/StrategyAllocations';

import { AccountAllocationEditor, AllocationTabFilter } from './AccountAllocationEditor';

interface AccountAllocationItemProps {
    account: Account;
    accountAllocation?: Allocation;
    inferredAllocation?: Allocation;
    cardIsEditing: boolean;
    allocationTypeFilter: AllocationTabFilter;
    filteredAllocations: Allocation[];
    isLoadingAllocations?: boolean;
    isLoadingPermissions?: boolean;
    onAllocationTypeFilterChange: (type: AllocationTabFilter) => void;
    onAccountManagementTypeChange: (accountId: GUID<Account>, managementType: ManagementType) => void;
    onAccountCashEqSweepChange: (accountId: GUID<Account>, cashEqSweep: boolean) => void;
    onAccountAllocationChange: (accountId: GUID<Account>, allocationGUID?: string) => void;
    onCreateAllocation?: (accountGUID: string) => void;
    onCloneAllocation?: (allocationGUID: string, accountGUID: string) => void;
}

export const AccountAllocationItem = ({
    account,
    accountAllocation,
    inferredAllocation,
    cardIsEditing,
    allocationTypeFilter,
    filteredAllocations,
    isLoadingAllocations,
    isLoadingPermissions,
    onAllocationTypeFilterChange,
    onAccountManagementTypeChange,
    onAccountCashEqSweepChange,
    onAccountAllocationChange,
    onCreateAllocation,
    onCloneAllocation,
}: AccountAllocationItemProps) => {
    return (
        <AccordionItem key={account.accountGUID} value={account.accountGUID} className="">
            <AccordionTrigger className="font-halyard rounded-none bg-gray-50 px-4 py-3 text-sm hover:bg-gray-100 data-[state=open]:bg-gray-100">
                <div className="flex w-full items-center justify-between pr-2">
                    <div className="flex flex-col items-start gap-1">
                        <div className="font-semibold text-gray-800">{account.accountName.plainText}</div>
                        <div className="text-xs font-normal text-gray-600">
                            {account.managementType === ManagementType.MANUAL
                                ? 'Manual'
                                : account.managementType === ManagementType.EXTERNAL
                                  ? 'External'
                                  : accountAllocation?.allocationName || 'No allocation'}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusBadge
                            status={account.lifecycleStatus}
                            liveDate={account.liveDate}
                            closedAt={account.closedAt}
                        />
                        <CopiableGuid className="h-5" guid={account.accountGUID} />
                    </div>
                </div>
            </AccordionTrigger>
            <AccordionContent>
                {cardIsEditing ? (
                    <AccountAllocationEditor
                        className="space-y-3 p-5"
                        managementType={account.managementType}
                        allocationGUID={account.allocationGUID}
                        onManagementTypeChange={(value) => onAccountManagementTypeChange(account.accountGUID, value)}
                        onAllocationGUIDChange={(value) => onAccountAllocationChange(account.accountGUID, value)}
                        allocationTypeFilter={allocationTypeFilter}
                        onAllocationTypeFilterChange={onAllocationTypeFilterChange}
                        filteredAllocations={filteredAllocations}
                        isLoadingAllocations={isLoadingAllocations}
                        isLoadingPermissions={isLoadingPermissions}
                        inferredAllocation={inferredAllocation}
                        afterManagementType={
                            account.managementType === ManagementType.AUTOMATIC && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">CashEQ Sweep</label>
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            checked={account.cashEqSweep}
                                            onCheckedChange={(checked) =>
                                                onAccountCashEqSweepChange(account.accountGUID, checked)
                                            }
                                        />
                                        <span>{account.cashEqSweep ? 'Yes' : 'No'}</span>
                                    </div>
                                </div>
                            )
                        }
                        onCreateAllocation={
                            onCreateAllocation ? () => onCreateAllocation(account.accountGUID) : undefined
                        }
                        onCloneAllocation={
                            onCloneAllocation
                                ? () =>
                                      account.allocationGUID &&
                                      onCloneAllocation(account.allocationGUID, account.accountGUID)
                                : undefined
                        }
                    />
                ) : account.managementType !== ManagementType.AUTOMATIC ? (
                    <div className="p-5 text-center text-sm text-gray-600">{titleCase(account.managementType)}</div>
                ) : accountAllocation ? (
                    <StrategyAllocations strategyAllocations={accountAllocation.strategyAllocations} />
                ) : (
                    <div className="p-5 text-center text-sm text-gray-500">No allocation</div>
                )}
            </AccordionContent>
        </AccordionItem>
    );
};
